/**
 * Ask Quinn — DOM-level controls for the Qualified docked launcher + drawer.
 *
 * Used by ThryvQualified.AskQuinn (and the top-level
 * hideAskQuinn / showAskQuinn / toggleAskQuinn wrappers in index.js).
 *
 * Targets the "Ask Quinn" experience specifically — the persistent dock
 * orb + chat drawer that mounts inside #qualified-multimodal-host. The
 * schedule-demo modal and classic chat are different Qualified experiences
 * and aren't affected by these methods.
 *
 * Approach: pure CSS, three rules.
 *
 *   1. Hide #qualified-multimodal-host
 *      Both the chat drawer AND the persistent launcher button render
 *      inside this host (via Shadow DOM). Hiding it removes both.
 *
 *   2. Hide #q-docked-skeleton
 *      Loading placeholder. Otherwise it appears in place of the host.
 *
 *   3. html[q-docked] { --q-docked-width: 0px }
 *      Collapses the dock-reserved layout space. Without this the page
 *      keeps a 400px gap on the right side even with the host hidden.
 *
 * SDK-level commands (teardown / setChatIsVisible / endExperience) don't
 * reliably stop the experience from deploying — verified empirically.
 * There is no documented client-side "kill" command; the official path
 * is a Qualified-side audience filter. This module is a stopgap.
 */

const AskQuinn = new Object();

const STYLE_ID = "thryv-ask-quinn-suppressed";
const LOADING_STYLE_ID = "thryv-ask-quinn-loading";
const LOADING_REVEAL_MS = 1800;

const SUPPRESSION_CSS =
  "#qualified-multimodal-host," +
  "#q-docked-skeleton{display:none !important;}" +
  "html[q-docked]{--q-docked-width:0px !important;}";

/** Inject the suppression CSS — drawer + launcher + skeleton hidden. */
AskQuinn.hide = function () {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = SUPPRESSION_CSS;

  (document.head || document.documentElement).appendChild(style);
};

/** Remove the suppression CSS — drawer + launcher render again. */
AskQuinn.show = function () {
  const style = document.getElementById(STYLE_ID);
  if (style) style.remove();
};

/** Flip current state: hidden → shown, shown → hidden. */
AskQuinn.toggle = function () {
  if (document.getElementById(STYLE_ID)) {
    AskQuinn.show();
  } else {
    AskQuinn.hide();
  }
};

/** Whether Ask Quinn is currently being suppressed. */
AskQuinn.isHidden = function () {
  return !!document.getElementById(STYLE_ID);
};

/**
 * Hide drawer + launcher during the loading phase. Same CSS as hide(),
 * different style id so it can be lifted independently of a permanent
 * suppression.
 */
AskQuinn.hideWhileLoading = function () {
  if (document.getElementById(LOADING_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = LOADING_STYLE_ID;
  style.textContent = SUPPRESSION_CSS;

  (document.head || document.documentElement).appendChild(style);
};

/** Remove the loading-phase suppression CSS. */
AskQuinn.showAfterLoading = function () {
  const style = document.getElementById(LOADING_STYLE_ID);
  if (style) style.remove();
};

/**
 * Fire `callback` after a fixed reveal delay, then dispatch
 * `qualified.ask-quinn-loaded`.
 *
 * We deliberately do NOT watch the DOM for "loaded" signals — Qualified's
 * skeleton element appears after the multimodal-host mounts, so any
 * "skeleton not present" check during that window fires prematurely and
 * the drawer flashes visible mid-load. A fixed delay sized to cover the
 * loading-pill phase is simple, predictable, and flicker-free.
 *
 * If the loading-phase suppression has already been lifted (e.g. another
 * caller invoked showAfterLoading() early), fires next tick.
 */
AskQuinn.onLoaded = function (callback) {
  const fire = function () {
    document.dispatchEvent(new CustomEvent("qualified.ask-quinn-loaded"));
    if (typeof callback === "function") callback();
  };

  if (!document.getElementById(LOADING_STYLE_ID)) {
    setTimeout(fire, 0);
    return;
  }

  setTimeout(fire, LOADING_REVEAL_MS);
};

/**
 * Watch the `q-docked` attribute on <html> and dispatch open/close events.
 *
 * Qualified toggles `q-docked="true"` on <html> when the drawer opens,
 * removes it when closed. Idempotent: subsequent calls are no-ops.
 *
 * Events:
 *   qualified.ask-quinn-opened — drawer opened
 *   qualified.ask-quinn-closed — drawer closed
 */
AskQuinn.watchStateChanges = function () {
  if (AskQuinn._stateObserver || !window.MutationObserver) return;

  let lastDocked = document.documentElement.hasAttribute("q-docked");

  const observer = new MutationObserver(function () {
    const docked = document.documentElement.hasAttribute("q-docked");
    if (docked === lastDocked) return;
    lastDocked = docked;
    document.dispatchEvent(
      new CustomEvent(docked ? "qualified.ask-quinn-opened" : "qualified.ask-quinn-closed"),
    );
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["q-docked"],
  });

  AskQuinn._stateObserver = observer;
};

/**
 * Read Qualified's persisted drawer state from the `__q_state_*` cookie.
 *
 * Cookie value is base64-encoded JSON. Qualified sets multiple
 * `__q_state_<id>` cookies (one slim `{uuid, cookieDomain}` and one
 * fuller messenger-state record); the messenger one carries `d: 1|0`
 * for docked-open / docked-closed. We pick the cookie that has `d`.
 *
 * @returns {{ docked: boolean, raw: object } | null}
 */
AskQuinn.getStoredState = function () {
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  let fallback = null;
  for (let i = 0; i < cookies.length; i++) {
    const eq = cookies[i].indexOf("=");
    const name = eq === -1 ? cookies[i] : cookies[i].slice(0, eq);
    if (name.indexOf("__q_state_") !== 0) continue;
    const value = eq === -1 ? "" : cookies[i].slice(eq + 1);
    try {
      const decoded = JSON.parse(atob(value));
      if (decoded && Object.prototype.hasOwnProperty.call(decoded, "d")) {
        return { docked: decoded.d === 1, raw: decoded };
      }
      if (!fallback) fallback = { docked: false, raw: decoded };
    } catch (e) {
      // skip malformed
    }
  }
  return fallback;
};

export default AskQuinn;
